import { Copy, Phone, PhoneMissed, Send } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { labelOf, PRODUCTS } from "../gulaConstants";
import { useCostSettings } from "../money/useMoneyData";
import { Field } from "../SimpleSelect";
import { phoneChatUrl } from "../whatsapp/whatsapp";
import { sendWhatsapp, WindowClosedError } from "../whatsapp/useWhatsapp";
import {
  clientName,
  emptyForm,
  formProblems,
  installedLabel,
  MAINTENANCE_OPTIONS,
  maintenanceSummary,
  SCRIPT_STEPS,
  type MaintenanceOffer,
  type WelcomeCall,
  type WelcomeForm,
} from "./welcome";
import { attemptWelcomeCall, saveWelcomeCall } from "./useWelcomeCalls";

/** Two or more exclusive choices drawn as pills. */
const Choice = <T extends string | number | boolean>({
  value,
  options,
  onChange,
}: {
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((option) => (
      <button
        key={String(option.value)}
        type="button"
        onClick={() => onChange(option.value)}
        className={cn(
          "rounded-full border px-3 py-1 text-sm transition-colors",
          value === option.value
            ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
            : "hover:bg-muted",
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const RATING = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label: String(value),
}));
const YES_NO = [
  { value: true, label: "Sí" },
  { value: false, label: "No" },
];

const linkMessage = (name: string | null, link: string, monthly: boolean) =>
  `Hola${name ? ` ${name}` : ""}, soy de Gula de Agua. Como hemos hablado, aquí tienes el enlace para activar el mantenimiento ${
    monthly ? "mensual (9,99 €/mes)" : "anual"
  }: ${link}\nCualquier duda, nos tienes en el 614 404 301.`;

/**
 * The call, step by step: how the installation went, what the client says,
 * and the maintenance offer. Saving as done logs the call (+3) and writes the
 * note on the client's sheet.
 */
export const WelcomeCallDialog = ({
  call,
  onClose,
  onChanged,
}: {
  call: WelcomeCall | null;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const { data: settings } = useCostSettings();
  const [form, setForm] = useState<WelcomeForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);

  useEffect(() => {
    setForm(call ? emptyForm(call) : null);
  }, [call]);

  if (!call || !form) return null;
  const set = <K extends keyof WelcomeForm>(key: K, value: WelcomeForm[K]) =>
    setForm({ ...form, [key]: value });
  const readOnly = call.status !== "pending";
  const problems = formProblems(form);
  const plan = maintenanceSummary(call);
  const link =
    form.maintenance_offer === "monthly"
      ? settings?.maintenance_monthly_link
      : form.maintenance_offer === "yearly"
        ? settings?.maintenance_yearly_link
        : null;

  const run = async (action: () => Promise<void>, toast?: string) => {
    setBusy(true);
    try {
      await action();
      if (toast) notify(toast, { type: "success" });
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const save = (done: boolean) =>
    run(
      async () => {
        await saveWelcomeCall(call.id, form, done);
        if (done) onClose();
      },
      done
        ? "Llamada guardada (+3). Todo lo que ha dicho está en su ficha"
        : "Borrador guardado",
    );

  const noAnswer = () =>
    run(async () => {
      await attemptWelcomeCall(call.id);
      onClose();
    }, "No contesta (+1). Mañana a las 10 vuelve a tu lista");

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    notify("Enlace copiado", { type: "info" });
  };

  const sendLink = () =>
    run(async () => {
      if (!link) return;
      const text = linkMessage(
        call.first_name,
        link,
        form.maintenance_offer === "monthly",
      );
      try {
        await sendWhatsapp(call.contact_id, text);
        notify("Enlace enviado por WhatsApp", { type: "success" });
      } catch (error) {
        if (error instanceof WindowClosedError && call.phone) {
          window.open(phoneChatUrl(call.phone, text), "_blank");
          return;
        }
        throw error;
      }
    });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Llamada de bienvenida · {clientName(call)}</DialogTitle>
          <DialogDescription>
            {[
              call.city,
              call.product ? labelOf(PRODUCTS, call.product) : null,
              call.installer_name ? `instaló ${call.installer_name}` : null,
              installedLabel(call),
              call.attempts
                ? `${call.attempts} ${call.attempts === 1 ? "intento" : "intentos"}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </DialogDescription>
        </DialogHeader>

        {call.phone && !readOnly ? (
          <Button asChild size="lg" className="w-full">
            <a href={`tel:${call.phone}`}>
              <Phone className="h-4 w-4" />
              Llamar al {call.phone}
            </a>
          </Button>
        ) : null}

        <button
          type="button"
          onClick={() => setScriptOpen(!scriptOpen)}
          className="text-left text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {scriptOpen ? "Ocultar el guion" : "Ver el guion de la llamada"}
        </button>
        {scriptOpen ? (
          <ol className="list-decimal space-y-1 rounded-md bg-muted/50 p-3 pl-7 text-sm">
            {SCRIPT_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : null}

        <fieldset disabled={readOnly} className="flex flex-col gap-4">
          <Field label="¿Fue bien la instalación?">
            <Choice
              value={form.install_ok}
              options={YES_NO}
              onChange={(value) => set("install_ok", value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="El instalador (1–5)">
              <Choice
                value={form.installer_rating}
                options={RATING}
                onChange={(value) => set("installer_rating", value)}
              />
            </Field>
            <Field label="Satisfacción con Gula (1–5)">
              <Choice
                value={form.satisfaction}
                options={RATING}
                onChange={(value) => set("satisfaction", value)}
              />
            </Field>
          </div>
          <Field label="¿Alguna incidencia?">
            <Choice
              value={form.has_issue}
              options={[
                { value: false, label: "No, todo bien" },
                { value: true, label: "Sí" },
              ]}
              onChange={(value) => set("has_issue", value)}
            />
            {form.has_issue ? (
              <Textarea
                value={form.issue_text}
                onChange={(event) => set("issue_text", event.target.value)}
                placeholder="Qué pasa, tal cual lo cuenta. Se abre una tarea para resolverlo."
                rows={2}
              />
            ) : null}
          </Field>
          <Field label="Notas: todo lo que diga">
            <Textarea
              value={form.notes}
              onChange={(event) => set("notes", event.target.value)}
              placeholder="Sabor del agua, presión, qué le gusta, qué le preocupa, si nos recomendaría…"
              rows={3}
            />
          </Field>

          <Field label="Mantenimiento">
            {call.has_maintenance || form.maintenance_offer === "has_it" ? (
              <p
                className={cn(
                  "rounded-md px-3 py-2 text-sm",
                  plan.tone === "warn"
                    ? "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100"
                    : "bg-lime-100 text-neutral-900 dark:bg-lime-300/20 dark:text-foreground",
                )}
              >
                {plan.detail}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-xs text-muted-foreground">{plan.detail}</p>
                {MAINTENANCE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => set("maintenance_offer", option.value)}
                    className={cn(
                      "flex items-baseline justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                      form.maintenance_offer === option.value
                        ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                        : "hover:bg-muted",
                    )}
                  >
                    <span className="font-medium">{option.label}</span>
                    {option.hint ? (
                      <span className="text-xs opacity-70">{option.hint}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
            {(["monthly", "yearly"] as MaintenanceOffer[]).includes(
              form.maintenance_offer as MaintenanceOffer,
            ) ? (
              link ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyLink}
                  >
                    <Copy className="h-4 w-4" />
                    Copiar enlace de pago
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={sendLink}
                  >
                    <Send className="h-4 w-4" />
                    Mandárselo por WhatsApp
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Falta el enlace de pago: lo configura un administrador en
                  Dinero → Comisiones.
                </p>
              )
            ) : null}
            {form.maintenance_offer === "thinking" ? (
              <Field label="Volver a ofrecerlo el">
                <Input
                  type="date"
                  value={form.maintenance_recontact_at}
                  onChange={(event) =>
                    set("maintenance_recontact_at", event.target.value)
                  }
                />
              </Field>
            ) : null}
            {form.maintenance_offer === "declined" ? (
              <Input
                value={form.maintenance_reason}
                onChange={(event) =>
                  set("maintenance_reason", event.target.value)
                }
                placeholder="Motivo (precio, ya tiene quien se lo haga, no lo ve necesario…)"
              />
            ) : null}
          </Field>
        </fieldset>

        {readOnly ? (
          <p className="text-sm text-muted-foreground">
            Llamada hecha
            {call.done_by_name ? ` por ${call.done_by_name}` : ""}
            {call.done_at
              ? ` el ${new Date(call.done_at).toLocaleDateString("es-ES")}`
              : ""}
            .
          </p>
        ) : (
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={noAnswer}
            >
              <PhoneMissed className="h-4 w-4" />
              No contesta
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => save(false)}
              >
                Guardar borrador
              </Button>
              <Button
                type="button"
                disabled={busy || problems.length > 0}
                title={problems.join(". ")}
                onClick={() => save(true)}
              >
                Guardar llamada
              </Button>
            </div>
          </DialogFooter>
        )}
        {!readOnly && problems.length ? (
          <p className="text-xs text-muted-foreground">
            {problems.join(" · ")}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
